import React, { useEffect, useState } from 'react'
import { CAlert, CCard, CCardBody, CCardHeader, CCol, CRow, CSpinner } from '@coreui/react'
import { CChartBar, CChartLine, CChartDoughnut } from '@coreui/react-chartjs'
import { api, currentUser } from '../api/client'

interface HomeKpi {
  bases: number; metas: number; cobradoHoy: number; prestadoHoy: number;
  gastadoHoy: number; utilidadBrutaMes: number; clientesTotales: number;
  clientesNuevos: number; clientesNuevosPeriodo: number;
  clientesRenovados: number; clientesEnRiesgo: number;
}
interface Graficas {
  anio: number; meses: string[]; cobrado: number[]; prestado: number[];
  gastado: number[]; utilidad: number[];
}

const AnaliticaPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [kpi, setKpi] = useState<HomeKpi | null>(null)
  const [graf, setGraf] = useState<Graficas | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<HomeKpi>(`/home/kpi?empresa=${empresa}`).then(setKpi).catch((e) => setError(e.message))
    api.get<Graficas>(`/home/graficas?empresa=${empresa}`).then(setGraf).catch(() => {})
  }, [empresa])

  if (error && !kpi) return <CAlert color="danger">{error}</CAlert>
  if (!kpi || !graf) return <div className="text-center p-5"><CSpinner /></div>

  const mesesCortos = graf.meses.map((m) => m.slice(0, 3))

  return (
    <>
      <h4 className="mb-1">Análisis histórico</h4>
      <p className="text-body-secondary mb-4">Serie histórica mensual de la operación — {graf.anio}</p>

      <CCard className="mb-4">
        <CCardHeader>
          <strong>Cobrado vs Prestado</strong> <small className="text-body-secondary">por mes — {graf.anio}</small>
        </CCardHeader>
        <CCardBody>
          <CChartBar
            data={{
              labels: mesesCortos,
              datasets: [
                { label: 'Cobrado', backgroundColor: '#078bcd', data: graf.cobrado },
                { label: 'Prestado', backgroundColor: '#285e8e', data: graf.prestado },
              ],
            }}
            options={{ maintainAspectRatio: false }}
            style={{ height: 300 }}
          />
        </CCardBody>
      </CCard>

      <CRow className="g-4 mb-4">
        <CCol md={7}>
          <CCard>
            <CCardHeader>
              <strong>Utilidad vs Gastado</strong> <small className="text-body-secondary">por mes</small>
            </CCardHeader>
            <CCardBody>
              <CChartLine
                data={{
                  labels: mesesCortos,
                  datasets: [
                    { label: 'Utilidad', borderColor: '#078bcd', backgroundColor: 'rgba(7,139,205,0.1)', data: graf.utilidad, fill: true, tension: 0.3 },
                    { label: 'Gastado', borderColor: '#d9534f', backgroundColor: 'rgba(217,83,79,0.1)', data: graf.gastado, fill: true, tension: 0.3 },
                  ],
                }}
                options={{ maintainAspectRatio: false }}
                style={{ height: 280 }}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={5}>
          <CCard>
            <CCardHeader>
              <strong>Clientes</strong> <small className="text-body-secondary">distribución</small>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: ['Nuevos', 'Renovados', 'En Riesgo'],
                  datasets: [{
                    backgroundColor: ['#078bcd', '#2E7D32', '#d9534f'],
                    data: [kpi.clientesNuevos, kpi.clientesRenovados, kpi.clientesEnRiesgo],
                  }],
                }}
                options={{ maintainAspectRatio: false }}
                style={{ height: 280 }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardHeader>
          <strong>Cobrado por mes</strong> <small className="text-body-secondary">serie completa</small>
        </CCardHeader>
        <CCardBody>
          <CChartBar
            data={{
              labels: mesesCortos,
              datasets: [
                { label: 'Cobrado', backgroundColor: graf.cobrado.map((_, i) => (i % 2 ? '#078bcd' : '#4db8e8')), data: graf.cobrado },
              ],
            }}
            options={{ maintainAspectRatio: false }}
            style={{ height: 240 }}
          />
        </CCardBody>
      </CCard>
    </>
  )
}

export default AnaliticaPage
